<template>
  <div class="page-container">
    <div class="content-wrapper">
      <!-- Header -->
      <PageHeader 
        :current-page="currentPage" 
        :islogin="islogin"
        :active-side="activeSide"
        :avatar="avatar"
        :nickname="nickname"
        @switch-side="handleSwitch"
        @logout="handleLogout"
      />

      <!-- Hero Section -->
      <section class="hero-section">
        <video 
          class="hero-bg-image" 
          autoplay
          loop
          muted
          playsinline
          preload="metadata"
        >
          <source src="./assets/video/background.mp4" type="video/mp4">
        </video>
        <div class="hero-text">
          <p class="hero-subtitle">还在找工作?不如来让工作主动找你!</p>
          <h1 class="hero-title">
            <span class="text1">屿智同行—</span>
            <span class="text2">千寻AI招聘平台</span>
          </h1>
          <div class="hero-stats">
            <span class="stat-item">1,400,000+大厂面试数据</span>
            <span class="stat-divider">|</span>
            <span class="stat-item">20,000+Offer数量</span>
            <span class="stat-divider">|</span>
            <span class="stat-item">80%+提高面试能力</span>
          </div>
          <div class="hero-buttons">
            <button class="btn btn-primary" @click="handleExperienceClick">
              立即体验
            </button>
            <span class="video-link" @click="playVideo">
              <span class="play-icon">▷</span>
              <span>观看宣传视频</span>
            </span>
          </div>
        </div>
      </section>

      <section v-if="currentPage === 'home'" class="core-features-section">
        <h2 class="core-features-title">
          <span class="title-main">核心功能</span>
          <span class="title-en">Core - Function</span>
        </h2>
        <div class="core-features-container">
          <div class="features-cards">
            <div v-for="(feature, index) in coreFeatures" :key="index" class="feature-card-item"
              :class="{ active: activeFeatureIndex === index }" @click="handleFeatureClick(index)">
              <h3 class="feature-card-title">{{ feature.title }}</h3>
              <div class="feature-card-description-wrapper">
                <p class="feature-card-description">{{ feature.description }}</p>
              </div>
            </div>
          </div>
          <div class="features-image">
            <div v-if="imageLoading && !allImagesLoaded" class="image-skeleton"></div>
            <div class="feature-images-container">
              <img 
                v-for="(feature, index) in coreFeatures" 
                :key="index"
                :src="preloadedImages[index] || getImageSrc(feature.image)" 
                :alt="feature.title"
                class="feature-image" 
                :class="{ 'image-active': activeFeatureIndex === index }"
                :loading="index === 0 ? 'eager' : 'lazy'"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section v-if="currentPage === 'home'" class="hot-jobs-companies-section">
        <div class="hot-content-wrapper">
          <HotJobGrid ref="hotJobGridRef" :limit="9" @view-more="goToHotJobs" />
          <HotCompanyGrid :limit="9" @view-more="goToHotJobsCompanies" />
        </div>
      </section>

      <section v-if="currentPage === 'home'" class="partners-section">
        <h2 class="partners-title">
          <span class="title-main">合作企业</span>
        </h2>
        <p class="partners-description">
          已为700+家企业简化面试流程,随时调阅候选人数据,自研大模型支持行业定制
        </p>

        <div class="partners-logo-container">
          <div class="partners-logo-track">
            <div v-for="(partner, index) in firstRowPartners" :key="`first-${index}`" class="partner-logo-item">
              <img 
                :src="partner.logo" 
                :alt="partner.name"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div v-for="(partner, index) in firstRowPartners" :key="`second-${index}`" class="partner-logo-item">
              <img 
                :src="partner.logo" 
                :alt="partner.name"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>

        <div class="partners-logo-container">
          <div class="partners-logo-track partners-logo-track-reverse">
            <div v-for="(partner, index) in secondRowPartners" :key="`row2-first-${index}`" class="partner-logo-item">
              <img 
                :src="partner.logo" 
                :alt="partner.name"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div v-for="(partner, index) in secondRowPartners" :key="`row2-second-${index}`" class="partner-logo-item">
              <img 
                :src="partner.logo" 
                :alt="partner.name"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>

        <div class="contact-us">
          <div class="btn-box">
            <button class="contact-button" @click.prevent="toggleQRCode">联系我们</button>
            
            <div id="qrCard" class="qr-card" :class="{ show: qrCodeVisible }" @click="toggleQRCode">
              <div class="qr-text">
                <div class="qr-title">扫一扫</div>
                <div class="qr-subtitle">期待与您更深入的沟通</div>
              </div>
              <img 
                v-if="qrCodeVisible"
                src="./assets/img/qrcode.png" 
                alt="联系我们二维码"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- 新手指引 -->
      <el-dialog
        :visible.sync="showNewbieGuide"
        :close-on-click-modal="false"
        :close-on-press-escape="true"
        :show-close="true"
        width="60%"
        custom-class="newbie-guide-dialog"
        @close="handleGuideClose"
      >
        <NewbieGuide 
          :visible="showNewbieGuide"
          @complete="handleGuideComplete"
        />
      </el-dialog>

      <footer v-if="currentPage === 'home'" class="company-info" ref="footerRef">
        <div class="footer-mask">
          <img 
            v-if="shouldLoadFooterImages"
            src="./assets/img/Mask.png" 
            alt="背景遮罩"
            loading="lazy"
            decoding="async"
            @load="onFooterImageLoad"
            @error="onFooterImageError"
          >
        </div>
        <div class="footer-logo">
          <img 
            v-if="shouldLoadFooterImages"
            src="./assets/img/company_logo.png" 
            alt="屿智同行"
            loading="lazy"
            decoding="async"
            @load="onFooterImageLoad"
            @error="onFooterImageError"
          >
          <!-- 占位符，在图片加载前显示 -->
          <div v-if="!shouldLoadFooterImages" class="footer-logo-placeholder"></div>
        </div>
        <div class="footer-beian">
          <a href="https://beian.miit.gov.cn/" target="_blank" style="">粤ICP备2025376732号-1</a>
        </div>
        <div class="footer-content">
          <div class="footer-left">
            <div class="footer-nav">
              <a href="#" class="nav-link">关于我们</a>
              <span class="nav-separator">|</span>
              <a href="#" class="nav-link">联系我们</a>
              <span class="nav-separator">|</span>
              <a href="#" class="nav-link">导师入驻</a>
            </div>
            <div class="company-name">深圳市屿智同行科技有限公司</div>
            <div class="company-address">地址:深圳市南山区丽水路2279号清华大学深圳国际研究生院</div>
            <div class="company-email">邮箱:yztx_zyh@163.com</div>
          </div>
        </div>
      </footer>

      <!-- 视频弹窗 -->
      <div v-if="videoDialogVisible" class="video-dialog-overlay" @click="closeVideoDialog">
        <div class="video-dialog" @click.stop>
          <button class="video-close-button" @click="closeVideoDialog" aria-label="关闭视频">×</button>
          <video 
            ref="videoPlayer"
            class="video-player"
            controls
            preload="none"
            :poster="videoPoster"
            @loadeddata="onVideoLoaded"
          >
            <source :src="videoSrc" type="video/mp4" />
            您的浏览器不支持视频播放。
          </video>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserInfo } from '@/api/user'
import PageHeader from '@/components/PageHeader/index.vue'
import HotCompanyGrid from '@/components/HotCompanyGrid.vue'
import HotJobGrid from '@/components/HotJobGrid.vue'
function loadImageForDirective(el, imageURL, vnode) {
  if (!imageURL) return;

  const defaultPlaceholder = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  el.src = defaultPlaceholder;

  // 创建临时图片对象预加载
  const tempImg = new Image();
  tempImg.onload = () => {
    el.src = tempImg.src;
    if (vnode && vnode.context && vnode.context.onImageLoad) {
      vnode.context.onImageLoad();
    }
    const loadEvent = new Event('load', { bubbles: false });
    el.dispatchEvent(loadEvent);
  };
  tempImg.onerror = () => {
    if (vnode && vnode.context && vnode.context.onImageLoad) {
      vnode.context.onImageLoad();
    }
  };
  const actualURL = typeof imageURL === 'string' ? imageURL : imageURL.default || imageURL;
  tempImg.src = actualURL;
}

const NewbieGuide = () => import('@/views/dashboards/components/NewbieGuide.vue')

export default {
  components: {
    HotCompanyGrid,
    HotJobGrid,
    PageHeader,
    NewbieGuide
  },
  data() {
    return {
      activeSide: 'seek',
      activeIndex: 0,
      activeFeatureIndex: 0,
      currentPage: 'home',
      islogin: false,
      isHeaderTransparent: true,
      videoDialogVisible: false,
      showUserMenu: false,
      avatar: '',
      nickname: '',
      imageLoading: false,
      preloadedImages: [], // 预加载的图片数组
      loadedImagesCount: 0, // 已加载的图片数量
      allImagesLoaded: false, // 所有图片是否已加载
      qrCodeVisible: false,
      autoPlayTimer: null, // 自动轮播定时器
      showNewbieGuide: false, // 是否显示新手指引
      newbieGuideWasShown: false, // 记录新手引导是否显示过
      shouldLoadFooterImages: false, // 是否应该加载footer图片
      footerObserver: null, // Intersection Observer实例
      coreFeatures: [
        {
          title: '直通企业人才库',
          description: '通过AI面试产出全方位面试记录,面试记录直通企业人才库,让Offer来找你。',
          image: require('./assets/img/1.webp')
        },
        {
          title: '专属AI面试教练',
          description: '聚焦你的简历与岗位JD,精准输出个性化且专业的面试问题,无痛积累面经。',
          image: require('./assets/img/2.webp')
        },
        {
          title: '职业发展规划大模型',
          description: '一款懂你、懂行业的职业发展规划大模型,基于你的个人特质与市场趋势,为你量身定制可落地、可迭代的专属职业发展路径。',
          image: require('./assets/img/3.webp')
        },
        {
          title: '一对一导师求职陪跑',
          description: '专属一对一求职陪跑导师,凭借资深行业经验与招聘视角,全程精准赋能,助力你高效冲刺顶级公司Offer。',
          image: require('./assets/img/4.webp')
        }
      ],
      partners: [
        { name: '腾讯', logo: require('./assets/img/logo/tx.png') },
        { name: '华为', logo: require('./assets/img/logo/hw.png') },
        { name: '百度', logo: require('./assets/img/logo/bd.png') },
        { name: '美团', logo: require('./assets/img/logo/mt.png') },
        { name: 'DJI', logo: require('./assets/img/logo/dj.png') },
        { name: 'OPPO', logo: require('./assets/img/logo/oppo.png') },
        { name: '科大讯飞', logo: require('./assets/img/logo/kdxf.png') },
        { name: '快手', logo: require('./assets/img/logo/ks.png') },
        { name: '京东', logo: require('./assets/img/logo/jd.png') },
        { name: '微软', logo: require('./assets/img/logo/ms.png') },
        { name: '亚马逊', logo: require('./assets/img/logo/amz.png') },
        { name: '西门子', logo: require('./assets/img/logo/sms.png') },
        { name: '联想', logo: require('./assets/img/logo/lx.png') },
        { name: '字节跳动', logo: require('./assets/img/logo/zjtd.png') }
      ]
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutside = (e) => {
          if (!el.contains(e.target)) binding.value()
        }
        document.addEventListener('click', el.clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutside)
      }
    },
    // 优化懒加载指令
    lazyload: {
      bind(el, binding, vnode) {
        // 保存组件实例引用
        el._lazyloadVnode = vnode;
      },
      inserted(el, binding) {
        loadImageForDirective(el, binding.value, el._lazyloadVnode);
      },
      update(el, binding) {
        if (binding.value !== binding.oldValue) {
          loadImageForDirective(el, binding.value, el._lazyloadVnode);
        }
      }
    }
  },
  methods: {
    handleSwitch(side) {
      this.activeSide = side;
    },
    handleLogout() {
      this.islogin = false
      this.avatar = ''
      this.nickname = ''
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      this.$message.success('已退出登录')
    },
    handleExperienceClick() {
      this.$store.commit('interviewState/CLEAR_CURRENT_INTERVIEW_JOB')
      this.$router.push('/store/entry/loginQRCode')
    },
    handleGuideComplete(formData) {
      // 新手指引完成后的处理
      const wasShown = this.newbieGuideWasShown
      this.showNewbieGuide = false
      this.newbieGuideWasShown = false
      
      // 可以将表单数据保存到 store 或发送到后端
      console.log('新手指引完成，表单数据:', formData)
      this.$message.success('信息填写完成！')
      
      // 如果新手引导显示过，重新调用热门岗位接口
      this.refreshHotJobsIfNeeded(wasShown)
    },
    handleGuideClose() {
      // 处理通过关闭按钮或遮罩层关闭新手引导
      const wasShown = this.newbieGuideWasShown
      this.newbieGuideWasShown = false
      
      // 如果新手引导显示过，重新调用热门岗位接口
      this.refreshHotJobsIfNeeded(wasShown)
    },
    refreshHotJobsIfNeeded(wasShown) {
      // 如果新手引导显示过，重新调用热门岗位接口
      if (wasShown && this.$refs.hotJobGridRef && this.currentPage === 'home') {
        this.$nextTick(() => {
          this.$refs.hotJobGridRef.fetchHotJobs()
        })
      }
    },
    async isLogin() {
      try {
        const res = await getUserInfo()
        if (res.data.code === 1) {
          const data = res.data.data || {}
          this.avatar = data.avatar || ''
          this.nickname = data.real_name || data.nickname || ''
          this.islogin = true
          const hasName = !!data.real_name
          const hasWorkStatus = !!(data.work_status || data.workStatus)
          
          // 如果核心字段不完善，显示新手引导
          const shouldShowGuide = !(hasName && hasWorkStatus)
          this.showNewbieGuide = shouldShowGuide
          this.newbieGuideWasShown = shouldShowGuide
        } else {
          this.islogin = false
          this.avatar = ''
          this.nickname = ''
          this.showNewbieGuide = false
        }
      } catch (e) {
        this.islogin = false
        this.avatar = ''
        this.nickname = ''
        this.showNewbieGuide = false
      }
    },
    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
      this.isHeaderTransparent = scrollTop < 50
    },
    goToHotJobs() {
      this.$router.push({ 
        name: 'hot-jobs',
        query: { tab: 'jobs' }
      })
    },
    goToHotJobsCompanies() {
      this.$router.push({ 
        name: 'hot-companies'
      })
    },
    playVideo() {
      this.videoDialogVisible = true
      // 延迟加载视频，避免弹窗打开时卡顿
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.$refs.videoPlayer.load()
        }
      })
    },
    closeVideoDialog() {
      // 先停止视频播放并重置
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        this.$refs.videoPlayer.currentTime = 0
        // 移除视频源，释放内存
        this.$refs.videoPlayer.src = ''
        this.$refs.videoPlayer.load()
      }
      // 延迟关闭弹窗，确保视频已停止
      this.$nextTick(() => {
        this.videoDialogVisible = false
      })
    },
    onVideoLoaded() {
      // 视频加载完成后的回调，可以在这里做一些优化
    },
    toggleQRCode() {
      this.qrCodeVisible = !this.qrCodeVisible
    },

    getImageSrc(imageURL) {
      const actualURL = typeof imageURL === 'string' ? imageURL : imageURL.default || imageURL;
      return actualURL;
    },
    handleFeatureClick(index) {
      if (this.activeFeatureIndex === index) return;
      // 用户手动点击时，重置自动轮播
      this.resetAutoPlay();
      this.activeFeatureIndex = index;
      if (this.allImagesLoaded) {
        this.imageLoading = false;
      }
    },
    // 切换到下一个功能
    nextFeature() {
      const nextIndex = (this.activeFeatureIndex + 1) % this.coreFeatures.length;
      this.activeFeatureIndex = nextIndex;
      if (this.allImagesLoaded) {
        this.imageLoading = false;
      }
    },
    startAutoPlay() {
      this.stopAutoPlay();
      this.autoPlayTimer = setInterval(() => {
        this.nextFeature();
      }, 4000);
    },
    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
    },
    resetAutoPlay() {
      this.stopAutoPlay();
      this.startAutoPlay();
    },
    preloadAllImages() {
      if (this.loadedImagesCount === 0) {
        this.imageLoading = true;
      }
      this.preloadedImages = new Array(this.coreFeatures.length).fill(null);
      this.loadedImagesCount = 0;
      this.allImagesLoaded = false;

      this.coreFeatures.forEach((feature, index) => {
        const imageURL = feature.image;
      const actualURL = typeof imageURL === 'string' ? imageURL : imageURL.default || imageURL;

      const tempImg = new Image();
      tempImg.onload = () => {
        this.preloadedImages[index] = tempImg.src;
        this.loadedImagesCount++;
        
        if (this.loadedImagesCount === this.coreFeatures.length) {
          this.allImagesLoaded = true;
          this.imageLoading = false;
        } else if (index === this.activeFeatureIndex) {
          this.imageLoading = false;
        }
      };
      tempImg.onerror = () => {
        console.error(`图片 ${index} 加载失败:`, actualURL);
        this.loadedImagesCount++;
        if (index === this.activeFeatureIndex) {
          this.imageLoading = false;
        }
        if (this.loadedImagesCount === this.coreFeatures.length) {
          this.allImagesLoaded = true;
          this.imageLoading = false;
        }
      };
      tempImg.src = actualURL;
      });
    },
    initFooterLazyLoad() {
      if (!this.$refs.footerRef) return
      
      if ('IntersectionObserver' in window) {
        this.footerObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.shouldLoadFooterImages = true
              if (this.footerObserver) {
                this.footerObserver.disconnect()
                this.footerObserver = null
              }
            }
          })
        }, {
          rootMargin: '200px 0px',
          threshold: 0.01
        })
        
        this.footerObserver.observe(this.$refs.footerRef)
      } else {
        this.initFooterScrollLoad()
      }
    },
    initFooterScrollLoad() {
      const checkFooterVisibility = () => {
        if (this.shouldLoadFooterImages) return
        
        if (this.$refs.footerRef) {
          const rect = this.$refs.footerRef.getBoundingClientRect()
          const windowHeight = window.innerHeight || document.documentElement.clientHeight
          
          if (rect.top < windowHeight + 200) {
            this.shouldLoadFooterImages = true
            window.removeEventListener('scroll', checkFooterVisibility)
          }
        }
      }
      
      // 初始检查一次
      checkFooterVisibility()
      // 监听滚动事件
      window.addEventListener('scroll', checkFooterVisibility, { passive: true })
    },
    // Footer图片加载成功回调
    onFooterImageLoad() {
    },
    // Footer图片加载失败回调
    onFooterImageError(e) {
      console.warn('Footer图片加载失败:', e.target.src)
    },
  },
  computed: {
    videoSrc() {
      return require('./assets/video/announce.mp4');
    },
    videoPoster() {
      return '';
    },
    firstRowPartners() {
      const mid = Math.ceil(this.partners.length / 2);
      return this.partners.slice(0, mid);
    },
    secondRowPartners() {
      const mid = Math.ceil(this.partners.length / 2);
      return this.partners.slice(mid);
    }
  },
  created() {
    this.preloadAllImages()
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.isLogin()
    // 启动自动轮播
    this.startAutoPlay()
    // 初始化footer图片懒加载
    this.initFooterLazyLoad()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    // 清除自动轮播定时器
    this.stopAutoPlay()
    // 清除Intersection Observer
    if (this.footerObserver) {
      this.footerObserver.disconnect()
      this.footerObserver = null
    }
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.pause()
      this.$refs.videoPlayer.src = ''
      this.$refs.videoPlayer.load()
    }
  }
}
</script>

<style scoped lang="scss">
$text-color: #656587;
$gradient-bg: linear-gradient(90deg, #62AFFF 0%, #9C41FF 100%);

.text1 {
  background: $gradient-bg;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.text2 {
  background: $gradient-bg;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-container {
  background: transparent;
  padding-bottom: 0;
  width: 100%;
  height: 100%;
  font-family: sans-serif;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
}


.hero-section {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100vw;
  height: 100vh;
  padding-top: 100px;
  margin-left: 20px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  .hero-bg-image {
    position: absolute;
    top: 0;
    left: 15%;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    /* 水平翻转视频，让其镜像播放 */
    transform: scaleX(-1) translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    -webkit-transform: scaleX(-1) translateZ(0);
    -moz-transform: scaleX(-1) translateZ(0);
    -ms-transform: scaleX(-1) translateZ(0);
    -o-transform: scaleX(-1) translateZ(0);
    will-change: auto;
    z-index: -2;
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
  }


  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0.98) 20%, rgba(255, 255, 255, 0.8) 30%, rgba(255, 255, 255, 0.7) 40%, rgba(255, 255, 255, 0) 60%),
      linear-gradient(to top, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.35) 3%, rgba(255, 255, 255, 0.2) 6%, rgba(255, 255, 255, 0.1) 8%, rgba(255, 255, 255, 0) 10%);
    background-blend-mode: overlay;
    z-index: -1;
  }
}

.hero-text {
  max-width: 800px;
  width: 100%;
  margin-bottom: 20px;
  padding: 40px 20px 40px 0;
  transform: translate(200px, -40px); // 向右移动20px，向上移动20px
}

.hero-title {
  font-size: 56px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 20px;
  font-weight: 500;
  background: $gradient-bg;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 16px;
  line-height: 1.4;
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}

.hero-stats .stat-item {
  font-size: 16px;
  color: #656587;
  line-height: 1.6;
  font-weight: 400;
}

.hero-stats .stat-divider {
  color: #656587;
  font-size: 16px;
}

.hero-buttons {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-buttons .btn-primary {
  padding: 16px 48px; // 增加内边距，让按钮更大更长
  border-radius: 12px; // 稍微增加圆角
  font-size: 18px; // 增加字体大小
  font-weight: 500;
  background: $gradient-bg;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex; // 使用flex布局
  align-items: center; // 垂直居中
  justify-content: center; // 水平居中
  white-space: nowrap;
  min-width: 210px; // 设置最小宽度，让按钮更长
  height: 56px; // 增加高度
}

.hero-buttons .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(98, 175, 255, 0.4);
}

.video-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4A4A4A;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
}

.video-link:hover {
  color: #62AFFF;
}

.play-icon {
  font-size: 16px;
  color: #4A4A4A;
}

.video-link:hover .play-icon {
  color: #62AFFF;
}

/* Core Features Section */
.core-features-section {
  padding: 80px 60px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.core-features-title {
  text-align: center;
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.core-features-title .title-main {
  background: $gradient-bg;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 38px;
  font-weight: 600;
}

.core-features-title .title-en {

  display: block;
    font-size: 24px;
    font-weight: bold;
    color: transparent; 
    letter-spacing: 2px; 

  -webkit-text-stroke: 1.5px #9C41FF;
  border: 1px solid;

  letter-spacing: 8px;

}

.core-features-container {
  display: flex;
  gap: 24px;
  align-items: stretch;
  max-width: 1400px;
  width: 100%;
}

.features-cards {
  flex: 0 0 380px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}


.feature-card-item {
  padding: 18px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: #fff;
  position: relative;

  display: grid;
  grid-template-rows: auto 0fr;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 10px;
    background: linear-gradient(90deg, #62AFFF 0%, #9C41FF 100%);
    opacity: 0.3;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 3px; 
    border-radius: 8px 8px 0 0;
    background: #fff;
    z-index: 1;
    transition: bottom 0.4s ease; 
  }


  >* {
    position: relative;
    z-index: 2;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(98, 175, 255, 0.1);

    &::before {
      opacity: 0.5; // hover时增加透明度
    }
  }

  &.active {
    background: linear-gradient(135deg, rgba(98, 175, 255, 0.05) 0%, rgba(156, 65, 255, 0.05) 100%);
    box-shadow: 0 4px 16px rgba(98, 175, 255, 0.2);
    grid-template-rows: auto 1fr;

    &::before {
      opacity: 1; // 选中时完全不透明
      bottom: -4px; // 选中时底部边框更粗（4px）
    }

    &::after {
      bottom: 4px; // 选中时底部留出4px空间
    }
  }
}

.feature-card-title {
  background: $gradient-bg;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.feature-card-description-wrapper {
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.4s ease 0.1s;

  .feature-card-item.active & {
    opacity: 1;
  }
}

.feature-card-description {
  color: #656587;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  padding-top: 8px;
}

.features-image {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 20px;
  min-width: 600px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 4px 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  min-height: 560px;
  height: 560px;
  position: relative;
}

.feature-images-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.image-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 8px;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.feature-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; // 填满容器，不留白
  object-position: center; // 居中显示
  border-radius: 8px;
  display: block;
    opacity: 0;
    visibility: hidden;
  transition: opacity 0.25s ease-in-out;
  pointer-events: none;
  z-index: 1;
  will-change: opacity;

  &.image-active {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    z-index: 2;
  }
}

.fade-enter-active {
  transition: clip-path 0.6s cubic-bezier(0.25, 0.8, 0.25, 1),
    opacity 0.6s ease;
}

.fade-leave-active {
  transition: clip-path 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
    opacity 0.4s ease;
}

.fade-enter {
  opacity: 0;
  clip-path: inset(100% 0 0 0);
}

.fade-enter-to {
  opacity: 1;
  clip-path: inset(0 0 0 0);
}

.fade-leave {
  opacity: 1;
  clip-path: inset(0 0 0 0);
}

.fade-leave-to {
  opacity: 0;
  clip-path: inset(0 0 100% 0);
}

// 热招岗位和热门企业 Section
.hot-jobs-companies-section {
  padding: 80px 60px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-image: url('./assets/img/bg2.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.9) 8%,
      rgba(255, 255, 255, 0.7) 18%,
      rgba(255, 255, 255, 0.5) 30%,
      rgba(255, 255, 255, 0.3) 45%,
      rgba(255, 255, 255, 0.25) 50%,
      rgba(255, 255, 255, 0.3) 55%,
      rgba(255, 255, 255, 0.5) 70%,
      rgba(255, 255, 255, 0.7) 82%,
      rgba(255, 255, 255, 0.9) 92%,
      rgba(255, 255, 255, 1) 100%
    );
    z-index: 0;
    pointer-events: none;
  }
  
  // 内容层在遮罩之上
  > * {
    position: relative;
    z-index: 1;
  }
}

.hot-content-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 企业合作 Section
.partners-section {
  padding: 80px 60px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-image: url('./assets/img/bg3.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: visible;
  position: relative;
  
  // 浅白色遮罩，上下边缘都过渡为白色
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.9) 6%,
      rgba(255, 255, 255, 0.7) 12%,
      rgba(255, 255, 255, 0.5) 20%,
      rgba(255, 255, 255, 0.35) 35%,
      rgba(255, 255, 255, 0.3) 65%,
      rgba(255, 255, 255, 0.35) 80%,
      rgba(255, 255, 255, 0.5) 88%,
      rgba(255, 255, 255, 0.7) 94%,
      rgba(255, 255, 255, 0.9) 97%,
      rgba(255, 255, 255, 1) 100%
    );
    z-index: 0;
    pointer-events: none;
  }
  
  // 内容层在遮罩之上
  > * {
    position: relative;
    z-index: 1;
  }
}

.partners-title {
  text-align: center;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.partners-title .title-main {
  background: $gradient-bg;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 38px;
  font-weight: 600;
}

.partners-description {
  text-align: center;
  color: #656587;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 60px;
  max-width: 800px;
}


.partners-logo-container {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  margin-bottom: 60px;
  overflow: hidden;
  position: relative;
}

.partners-logo-track {
  display: flex;
  gap: 40px;
  animation: scroll-left 30s linear infinite; // 加快速度
  width: fit-content;
  // logo从容器右侧开始，向左滚动
  transform: translateX(0);
}

// 第二行和第一行效果一样，只是稍微延迟
.partners-logo-track-reverse {
  animation: scroll-left 30s linear infinite; // 和第一行一样的动画和速度
  animation-delay: -2s; // 稍微延迟，让两行不同步
}

.partner-logo-item {
  flex-shrink: 0;
  width: 220px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.8);
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: grayscale(20%);
    transition: filter 0.3s ease;
  }

  &:hover img {
    filter: grayscale(0%);
  }
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}


.contact-us {
  margin-top: 40px;
}

.contact-us .btn-box {
  position: relative;
  display: inline-block;
}

.contact-button {
  display: inline-block;
  padding: 10px 25px;
  background: var(--linear,
      linear-gradient(-39.22deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.6) 100%));
  border-radius: 25px;
  color: #f0fbff;
  text-align: center;
  font-family: "AdventPro-SemiBold", sans-serif;
  font-size: 22px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  background-color: #5545e0;
  box-shadow: 0 4px 12px rgba(106, 85, 255, 0.3);
}

.qr-card {
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-bottom: 12px;
  width: 240px;
  padding: 20px;
  background: #fff;
  border: 1px solid rgba(255,255,255,.6);
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0,0,0,.12);
  transform-origin: center bottom;
  transform: translate(-50%, 14px) scale(.35);
  opacity: 0;
  pointer-events: none;
  transition: transform .35s cubic-bezier(.4,0,.2,1),
             opacity .28s ease;
  backdrop-filter: blur(4px);
  z-index: 1000;
}

.qr-card.show {
  transform: translate(-50%, 0) scale(1);
  opacity: 1;
  pointer-events: auto;
}

.qr-text {
  text-align: center;
  margin-bottom: 15px;
}

.qr-title {
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(90deg, #77BAFF 0%, #9C41FF 100%),
              linear-gradient(0deg, rgba(132, 172, 237, 0.4), rgba(132, 172, 237, 0.4));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 5px;
  line-height: 1.2;
}

.qr-subtitle {
  font-size: 13px;
  background: linear-gradient(90deg, #77BAFF 0%, #9C41FF 100%),
              linear-gradient(0deg, rgba(132, 172, 237, 0.4), rgba(132, 172, 237, 0.4));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
}

.qr-card img {
  width: 100%;
  display: block;
  border-radius: 8px;
}

.qr-card::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 10px 8px 0 8px;
  border-color: #fff transparent transparent transparent;
  filter: drop-shadow(0 4px 3px rgba(0,0,0,.08));
}



.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.development-dialog {
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
}

.development-content {
  text-align: center;
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.development-image {
  max-width: 300px;
  height: auto;
  border-radius: 8px;
}

.development-text {
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin: 0;
}

.close-button {
  background: linear-gradient(90deg, #7eb4eb 0%, #b16ef9 100%);
  border: none;
  border-radius: 6px;
  padding: 10px 30px;
  font-size: 14px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(126, 180, 235, 0.4);
  }
}

// 视频弹窗样式
.video-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.video-dialog {
  position: relative;
  width: 90vw;
  max-width: 1200px;
  max-height: 90vh;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: scaleIn 0.3s ease;
  transform-origin: center;
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.video-close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #fff;
    transform: rotate(90deg);
  }

  &:active {
    transform: rotate(90deg) scale(0.95);
  }
}

.video-player {
  width: 100%;
  height: auto;
  max-height: 90vh;
  display: block;
  outline: none;
  background: #000;
}

footer {
  background-color: #6A6985;
  color: #fff;
  padding: 40px 0;
  font-size: 14px;
  line-height: 1.6;
  position: relative;
  overflow: hidden;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-bottom: 0;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.footer-left {
  flex: 1;
  text-align: left;
}

.footer-nav {
  margin-bottom: 20px;
}

.nav-link {
  color: #fff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #f0f0f0;
}

.nav-separator {
  margin: 0 10px;
  color: #fff;
}

.company-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #fff;
}

.company-address,
.company-email {
  font-size: 16px;
  margin-bottom: 8px;
  color: #fff;
}

.footer-mask {
  position: absolute;
  right: -13vw;
  z-index: 1;
  pointer-events: none;
  width: 80%;
  height: 95%;
}

.footer-mask img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.footer-logo {
  position: absolute;
  top: 50%;
  right: 175px;
  transform: translateY(-50%);
  z-index: 2;
}

.footer-beian{
  position: absolute;
  bottom: 0;
  right: 200px;
  transform: translateY(-50%);
  z-index: 2;
}
.footer-logo img {
  width: 100%;
  position: relative; // 确保在主图层
  z-index: 1;
  transition: opacity 0.3s ease;
}

.footer-logo-placeholder {
  width: 100%;
  height: 60px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.3;
  }
}

.content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media (max-width: 768px) {
  .content-wrapper {
    padding: 0 10px;
  }

  // Hero Section 移动端适配
  .hero-section {
    width: 100%;
    height: auto;
    min-height: 100vh;
    padding-top: 160px; // 增加 padding-top 避免被 header 遮挡
    padding-bottom: 40px;
    margin-left: 0;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    .hero-bg-image {
      left: 0;
      width: 100%;
      object-position: center;
  }

    &::after {
      background: linear-gradient(to bottom, 
        rgba(255, 255, 255, 0.98) 0%, 
        rgba(255, 255, 255, 0.9) 15%, 
        rgba(255, 255, 255, 0.75) 30%, 
        rgba(255, 255, 255, 0.5) 50%, 
        rgba(255, 255, 255, 0.25) 70%, 
        rgba(255, 255, 255, 0.1) 85%, 
        rgba(255, 255, 255, 0) 100%
      );
  }
}

  .hero-text {
    max-width: 100%;
    padding: 20px;
    transform: none;
    text-align: center;
    margin-bottom: 0;
  }

  .hero-title {
    font-size: 32px;
    margin-bottom: 16px;
    text-align: center;
    line-height: 1.3;
  }

  .hero-subtitle {
    font-size: 16px;
    text-align: center;
    margin-bottom: 12px;
    line-height: 1.5;
  }

  .hero-stats {
    justify-content: center;
    gap: 8px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .hero-stats .stat-item {
    font-size: 14px;
  }

  .hero-stats .stat-divider {
    font-size: 14px;
  }

  .hero-buttons {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .hero-buttons .btn-primary {
    width: 100%;
    max-width: 280px;
    height: 48px;
    font-size: 16px;
    min-width: auto;
    padding: 12px 24px;
  }

  .video-link {
    font-size: 14px;
  }

  // Core Features Section 移动端适配
  .core-features-section {
    padding: 40px 20px;
  }

  .core-features-title {
    margin-bottom: 24px;
  }

  .core-features-title .title-main {
    font-size: 28px;
  }

  .core-features-title .title-en {
    font-size: 14px;
    letter-spacing: 4px;
  }

  .core-features-container {
    flex-direction: column;
    gap: 24px;
  }

  .features-cards {
    flex: 1;
    width: 100%;
  }

  .feature-card-item {
    padding: 16px;
    margin-bottom: 12px;
  }

  .feature-card-title {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .feature-card-description {
    font-size: 13px;
    line-height: 1.6;
  }

  .features-image {
    min-width: auto;
    width: 100%;
    min-height: 300px;
    height: 300px;
  }

  // Hot Jobs/Companies Section 移动端适配
  .hot-jobs-companies-section {
    padding: 40px 20px;
  }

  .hot-content-wrapper {
    gap: 40px;
    }

  // Partners Section 移动端适配
  .partners-section {
    padding: 40px 20px;
  }

  .partners-title {
    margin-bottom: 20px;
  }

  .partners-title .title-main {
    font-size: 28px;
  }

  .partners-description {
    font-size: 14px;
    padding: 0 20px;
    text-align: center;
    line-height: 1.6;
  }

  .partners-logo-container {
    margin: 20px 0;
  }

  .partner-logo-item {
    min-width: 120px;
    height: 60px;
    padding: 8px;
  }

  // Footer 移动端适配
  .company-info {
    padding: 40px 20px;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: 24px;
    padding: 0;
  }

  .footer-left {
    text-align: center;
  }

  .footer-nav {
    margin-bottom: 16px;
  }

  .nav-link {
    font-size: 14px;
  }

  .nav-separator {
    margin: 0 8px;
  }

  .company-name {
    font-size: 14px;
    margin-bottom: 12px;
  }

  .company-address,
  .company-email {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .footer-mask {
    width: 100%;
    height: 50%;
  }

  .footer-logo {
    position: relative;
    top: auto;
    right: auto;
    transform: none;
    width: 200px;
    height: auto;
    margin: 20px auto;
  }

  .footer-logo img {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding-top: 180px; // 小屏时 header 更高，需要更多 padding
    padding-bottom: 30px;
    min-height: auto;
  }

  .hero-text {
    padding: 16px;
  }

  .hero-title {
    font-size: 28px;
    margin-bottom: 12px;
  }

  .hero-subtitle {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .hero-stats {
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
  }

  .hero-stats .stat-item {
    font-size: 13px;
  }

  .hero-stats .stat-divider {
    display: none;
  }

  .hero-buttons {
    gap: 10px;
  }

  .hero-buttons .btn-primary {
    max-width: 100%;
    height: 44px;
    font-size: 15px;
  }

  .video-link {
    font-size: 13px;
  }

  // Core Features Section 小屏适配
  .core-features-section {
    padding: 30px 16px;
  }

  .core-features-title {
    margin-bottom: 20px;
  }

  .core-features-title .title-main {
    font-size: 24px;
  }

  .core-features-title .title-en {
    font-size: 12px;
    letter-spacing: 2px;
  }

  .core-features-container {
    gap: 20px;
  }

  .feature-card-item {
    padding: 14px;
    margin-bottom: 10px;
  }

  .feature-card-title {
    font-size: 16px;
    margin-bottom: 6px;
  }

  .feature-card-description {
    font-size: 12px;
  }

  .features-image {
    min-height: 250px;
    height: 250px;
  }

  // Hot Jobs/Companies Section 小屏适配
  .hot-jobs-companies-section {
    padding: 30px 16px;
  }

  .hot-content-wrapper {
    gap: 30px;
  }

  // Partners Section 小屏适配
  .partners-section {
    padding: 30px 16px;
  }

  .partners-title {
    margin-bottom: 16px;
  }

  .partners-title .title-main {
    font-size: 24px;
  }

  .partners-description {
    font-size: 13px;
    padding: 0 16px;
  }

  .partner-logo-item {
    min-width: 100px;
    height: 50px;
    padding: 6px;
  }

  // Footer 小屏适配
  .company-info {
    padding: 30px 16px;
  }

  .footer-content {
    gap: 20px;
  }

  .nav-link {
    font-size: 13px;
  }

  .nav-separator {
    margin: 0 6px;
}

  .company-name {
    font-size: 13px;
    margin-bottom: 10px;
  }

  .company-address,
  .company-email {
    font-size: 12px;
    margin-bottom: 5px;
}

  .footer-logo {
    width: 160px;
    margin: 16px auto;
  }
}

// 浏览器优化提示
.feature-card-item,
.feature-card-description-wrapper,
.feature-image {
  will-change: transform, opacity;
}

// Hide all scrollbars
* {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

*::-webkit-scrollbar {
  display: none;
}


::v-deep .newbie-guide-dialog {
  .el-dialog__body {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .el-dialog__header {
    display: none;
  }
}
</style>